import { LabelMesh } from './LabelMesh';
import { LabelMeshType } from '../types';
import { DeviceInfo } from '../utils/DeviceInfo';
import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import * as THREE from 'three';
import { POI } from '../poi/POI';
import { Color } from 'three';

/**
 * CanvasLabelMesh - Canvas-based text rendering for WebGL compatibility
 * Follows proper OOP hierarchy extending LabelMesh
 * Uses marked library for consistent markdown parsing
 * 
 * ⚠️ IMPORTANT: This class uses html2canvas which internally calls document.write()
 * This is a known limitation of the html2canvas library. The browser may show warnings
 * about document.write() usage. These warnings have been minimized but not eliminated.
 * 
 * 🛠️ TO COMPLETELY AVOID WARNINGS:
 * - For all instances: CanvasLabelMesh.setGlobalUseHtml2Canvas(false);
 * - For specific instances: labelMesh.setUseHtml2Canvas(false);
 * This will use pure canvas text rendering instead of html2canvas.
 * 
 * The fallback rendering provides basic text display but loses rich HTML/markdown formatting.
 * 
 * Alternative: Could use Showdown.js (https://showdownjs.com/) instead of marked
 * for markdown-to-HTML conversion if preferred. Both provide similar functionality.
 */
export class CanvasLabelMesh extends LabelMesh {
    private _canvas: HTMLCanvasElement | null = null;
    private _context: CanvasRenderingContext2D | null = null;
    private _texture: any = null; // Will be CanvasTexture in real implementation
    private _renderedText: string = '';
    private _useHtmlRendering: boolean = true; // Re-enabled: HTML rendering for rich text (now event-driven, so much faster!)
    private _useHtml2Canvas: boolean = true; // Set to false to avoid document.write() warnings entirely
    
    // Global setting for all new instances.
    // MOBILE: html2canvas clones the DOM + renders through an iframe per label. Creating
    // many labels in a burst (e.g. 45 map pins) OOM-kills the tab on iOS Safari — observed
    // as a hard reload loop ~2s after start. Mobile uses the lightweight ctx.fillText
    // fallback renderer instead; desktop keeps the rich html2canvas path.
    private static _globalUseHtml2Canvas: boolean = DeviceInfo.isDesktop;
    


    constructor(poi: POI) {
        super(poi, 'CanvasLabelMesh');
        this._useHtml2Canvas = CanvasLabelMesh._globalUseHtml2Canvas;
        this.initializeMarkdownRenderer();
    }

    /**
     * Configure whether to use html2canvas or always use fallback rendering (globally for all new instances)
     * Set to false to completely avoid document.write() warnings across the entire application
     */
    public static setGlobalUseHtml2Canvas(enabled: boolean): void {
        CanvasLabelMesh._globalUseHtml2Canvas = enabled;
        //console.log(`🎨 CanvasLabelMesh: html2canvas usage globally ${enabled ? 'ENABLED' : 'DISABLED'} for all new instances`);
    }

    /**
     * Configure whether to use html2canvas or always use fallback rendering (for this instance)
     * Set to false to completely avoid document.write() warnings
     */
    public setUseHtml2Canvas(enabled: boolean): void {
        this._useHtml2Canvas = enabled;
        //console.log(`🎨 CanvasLabelMesh: html2canvas usage ${enabled ? 'ENABLED' : 'DISABLED'} for ${this.poi.name}`);
    }

    /**
     * Create a fallback canvas when html2canvas fails or to avoid document.write() issues
     */
    private createFallbackCanvas(element: HTMLElement, _width: number, _height: number): HTMLCanvasElement {
        // Settings-aware fallback (used on mobile, where html2canvas is disabled, and when
        // html2canvas fails). Honors label font size/family/color and background color incl.
        // full transparency — the old version hardcoded a white box with black 16px text.
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return canvas;

        const fontSize = Math.max(8, (this.poi.poidata.labelFontSize.value ?? 16)) * 3; // 3x supersampling for crisp text
        const fontFamily = this.poi.poidata.labelFontFamily.value ?? 'Arial';
        const textColor = (this.poi.poidata.labelColor.value ?? new Color('#ffffff')).toString();
        const bg = this.poi.poidata.labelBackgroundColor.value ?? new Color('rgba(0,0,0,0.7)');
        const bgAlpha = (bg as any)?.a ?? 1;

        // innerText zuerst: es liefert Zeilenumbrueche zwischen Block-Elementen,
        // textContent verklebt Titel und Fliesstext zu einer Wurst.
        const textContent = (element.innerText || element.textContent || '').trim() || 'Label';
        const paragraphs = textContent.split('\n').map(l => l.trim()).filter(l => l.length > 0);

        const font = `${fontSize}px ${fontFamily}, sans-serif`;
        ctx.font = font;

        // Wortumbruch gegen labelMaxWidth. Der HTML-Pfad wickelt per CSS max-width
        // bei 48px Grundschrift — hier ohne DOM muss ctx.measureText das leisten,
        // sonst rendert jede Mobile-Caption als EINE beliebig breite Zeile.
        // Skalierung auf die Fallback-Schriftgroesse haelt die Zeichen-pro-Zeile
        // identisch zum Desktop-Pfad.
        const userMaxWidth = this.poi.poidata.labelMaxWidth?.value ?? 0;
        const wrapWidth = userMaxWidth > 0 ? userMaxWidth * (fontSize / 48) : Infinity;
        const lines: string[] = [];
        for (const para of paragraphs) {
            if (!isFinite(wrapWidth)) { lines.push(para); continue; }
            let line = '';
            for (const word of para.split(/\s+/)) {
                const probe = line ? line + ' ' + word : word;
                if (ctx.measureText(probe).width <= wrapWidth || !line) {
                    line = probe;
                } else {
                    lines.push(line);
                    line = word;
                }
            }
            if (line) lines.push(line);
        }

        const pad = fontSize * 0.4;
        const lineHeight = fontSize * 1.3;
        const textWidth = Math.max(...lines.map(l => ctx.measureText(l).width), 1);
        canvas.width = Math.ceil(textWidth + pad * 2);
        canvas.height = Math.ceil(lines.length * lineHeight + pad * 2);

        // Canvas resize resets context state — set everything again.
        ctx.font = font;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        if (bgAlpha > 0.001) {
            ctx.fillStyle = bg.toString();
            const r = Math.min(fontSize * 0.4, canvas.height / 2);
            ctx.beginPath();
            (ctx as any).roundRect ? (ctx as any).roundRect(0, 0, canvas.width, canvas.height, r) : ctx.rect(0, 0, canvas.width, canvas.height);
            ctx.fill();
        }

        ctx.fillStyle = textColor;
        lines.forEach((line, i) => {
            ctx.fillText(line, canvas.width / 2, pad + lineHeight * (i + 0.5));
        });

        return canvas;
    }

    /**
     * Override the parent's buildMesh method. Canvas labels need a properly configured material.
     */
    protected buildMesh(): void {
        this.dispose(); // Clean up existing mesh if any
        this._geometry = this.createGeometry();
        
        // Create a proper material that can be updated with the canvas texture
        ////console.log(`[CanvasLabelMesh:${this.poi.name}] buildMesh - creating material for canvas texture`);
        const material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            opacity: 1,
            alphaTest: 0.1,
            side: THREE.DoubleSide
        });
        
        this._mesh = new THREE.Mesh(this._geometry, material);
        // matrixAutoUpdate is managed by SceneNode.syncTransformToThreeJS()
        
        // Use the base class helper to ensure consistent bounding visualization
        this.finalizeMeshSetup();
        
        ////console.log(`[CanvasLabelMesh:${this.poi.name}] buildMesh completed - mesh created for canvas texture`);
    }

    /**
     * Initialize the text renderer for canvas text extraction
     */
    private initializeMarkdownRenderer(): void {
        // No longer needed since we receive HTML directly from POIData
        // The markdown-to-HTML conversion is handled in POIData.setupMarkdownParsing()
    }

    /**
     * Convert HTML to plain text for canvas rendering
     */
    private markdownToPlainText(html: string): string {
        try {
            // Create temporary element to extract text content from HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // Extract plain text, preserving line breaks
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            
            // Clean up and return
            return textContent.trim();
            
        } catch (error) {
            console.error('Failed to convert HTML to plain text:', error);
            return html; // Fallback to original text
        }
    }

    /**
     * Initialize the canvas mesh
     */
    async initialize(): Promise<void> {
        try {
            // Create canvas
            this._canvas = document.createElement('canvas');
            this._context = this._canvas.getContext('2d');
            
            if (!this._context) {
                throw new Error('Could not get 2D context from canvas');
            }
            
            this._isInitialized = true;

            // Texture is now created here to ensure it exists before updateContent is called.
            this._texture = new THREE.CanvasTexture(this._canvas!);
            this._texture.colorSpace = THREE.SRGBColorSpace;
            
            // Re-enable canvas rendering - use HTML content (auto-generated from markdown)
            const text = this.poi.poidata.html.value || 'DEBUG LABEL';
            this.updateContent(text);
            
        } catch (error) {
            console.error(`❌ [CANVAS:${this.poi.name}] INITIALIZE FAILED:`, error);
            throw error;
        }
    }

    /**
     * Update canvas content with new text - supports both plain text and rich HTML rendering
     */
    updateContent(text: string): void {
        if (this._useHtmlRendering) {
            this.updateContentWithHtml(text);
        } else {
            this.updateContentWithPlainText(text);
        }
    }

    /**
     * Rich HTML-to-canvas rendering (preserves markdown formatting)
     */
    private async updateContentWithHtml(text: string): Promise<void> {
        try {
            if (!this._isInitialized) return;
            // Recreate canvas/context if they were disposed by a prior lifecycle
            if (!this._canvas || !this._context) {
                this._canvas = document.createElement('canvas');
                this._context = this._canvas.getContext('2d');
                if (!this._context) {
                    console.error(`[CANVAS:${this.poi.name}] Failed to get 2D context after recreation`);
                    return;
                }
                if (!this._texture) {
                    this._texture = new THREE.CanvasTexture(this._canvas);
                    this._texture.colorSpace = THREE.SRGBColorSpace;
                }
            }

            // Validate input text
            if (!text || text.trim().length === 0) {
                console.warn(`[CANVAS:${this.poi.name}] Empty text provided, using fallback`);
                text = "Empty Label";
            }

            // 1. The text parameter is already HTML (converted from markdown in POIData)
            const cleanHtml = DOMPurify.sanitize(text, {
                ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'code', 'pre', 'ul', 'ol', 'li', 'blockquote'],
                ALLOWED_ATTR: ['class', 'style']
            });

            // 2. Create temporary DOM element for rendering.
            // Width must be >= wrapper max-width so the text isn't wrapped by the
            // viewport on narrow screens (mobile). The wrapper itself handles its
            // own sizing via fit-content + max-width.
            const renderContainer = document.createElement('div');
            renderContainer.style.cssText = `
                position: absolute;
                top: -9999px;
                left: -9999px;
                width: 4000px;
                height: auto;
            `;

            const wrapper = document.createElement('div');
            
            // 🔧 FIX: Use consistent base font size for all labels
            // This ensures same font size regardless of content amount
            const BASE_FONT_SIZE = 48; // Fixed base font size for consistency
            const fontFamily = this.poi.poidata.labelFontFamily.value ?? 'Arial';
            const textColor = (this.poi.poidata.labelColor.value ?? new Color('#ffffff')).toString();
            const bgColor = (this.poi.poidata.labelBackgroundColor.value ?? new Color('#000000')).toString();
            const bgGradient = this.poi.poidata.labelBackgroundGradient.value;
            const bgMode = this.poi.poidata.labelBackgroundMode.value || 'line';

            // Honor labelMaxWidth — 0 means no wrap (single line, intrinsic width).
            const userMaxWidth = this.poi.poidata.labelMaxWidth?.value ?? 0;
            const wrapStyle = userMaxWidth > 0
                ? `max-width: ${userMaxWidth}px; word-wrap: break-word; white-space: normal;`
                : `white-space: nowrap;`;
            wrapper.style.cssText = `
                font-family: ${fontFamily};
                font-size: ${BASE_FONT_SIZE}px;
                color: ${textColor};
                display: inline-block;
                line-height: 1.4;
                ${wrapStyle}
            `;

            if (bgMode !== 'line') {
                // Apply gradient or solid background
                if (bgGradient && bgGradient.trim()) {
                    wrapper.style.background = bgGradient;
                    wrapper.style.backgroundColor = ''; // Clear solid color
                } else {
                    wrapper.style.backgroundColor = bgColor;
                    wrapper.style.background = ''; // Clear gradient
                }
                wrapper.style.padding = '10px 20px';
                wrapper.style.borderRadius = '8px';
            }

            // Validate HTML output and set content
            if (!cleanHtml || cleanHtml.trim().length === 0) {
                console.warn(`[CANVAS:${this.poi.name}] Empty HTML after processing, using fallback`);
                wrapper.innerHTML = "Invalid Content";
            } else {
                wrapper.innerHTML = cleanHtml;
            }

            // Apply line-specific styling if needed
            if (bgMode === 'line') {
                // 🎨 TRUE LINE-BY-LINE HIGHLIGHTING: CSS-only approach that preserves HTML formatting
                
                // Apply line-by-line highlighting to all text elements
                const textElements = wrapper.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, blockquote, pre, div');
                textElements.forEach(elm => {
                    const element = elm as HTMLElement;
                    // Apply gradient or solid background for line mode
                    const lineBackground = (bgGradient && bgGradient.trim()) ? bgGradient : bgColor;
                    element.style.cssText += `
                        background: ${lineBackground};
                        background-clip: padding-box;
                        box-decoration-break: clone;
                        -webkit-box-decoration-break: clone;
                        padding: 10px 20px;
                        border-radius: 10px;
                        line-height: 1.5em;
                        display: inline;
                        margin: 0em 0em;
                        white-space: pre-wrap;
                        word-break: break-word;
                    `;
                });
                
                // Special handling for inline elements within text
                const inlineElements = wrapper.querySelectorAll('strong, em, b, i, code, a, span');
                inlineElements.forEach(elm => {
                    const element = elm as HTMLElement;
                    element.style.cssText += `
                        background: inherit;
                        box-decoration-break: clone;
                        -webkit-box-decoration-break: clone;
                    `;
                });
                
                // Apply overall wrapper styling for better line separation
                wrapper.style.cssText += `
                    word-wrap: break-word;
                    white-space: pre-wrap;
                `;
            }

            renderContainer.appendChild(wrapper);
            document.body.appendChild(renderContainer);

            // Ensure wrapper has minimum dimensions before rendering (replaces onclone logic)
            const wrapperStyle = window.getComputedStyle(wrapper);
            if (parseInt(wrapperStyle.width) < 1) {
                wrapper.style.minWidth = '1px';
            }
            if (parseInt(wrapperStyle.height) < 1) {
                wrapper.style.minHeight = '1px';
            }

            // 3. Render HTML to canvas using html2canvas (or fallback if disabled)
            let renderedCanvas: HTMLCanvasElement;
            
            if (!this._useHtml2Canvas) {
                // Use fallback rendering to completely avoid document.write() warnings
                //console.log(`🎨 Using fallback canvas rendering for ${this.poi.name} (html2canvas disabled)`);
                renderedCanvas = this.createFallbackCanvas(wrapper, Math.max(wrapper.offsetWidth, 1), Math.max(wrapper.offsetHeight, 1));
            } else {
                // Note: html2canvas internally uses document.write() which cannot be completely avoided
                // These settings minimize but don't eliminate browser warnings
                try {
                    // Ensure fonts/layout are ready before capture to avoid 0x0 or late-load issues
                    try { await (document as any).fonts?.ready; } catch {}
                    await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));

                    // Debug context (kept silent by default)

                    renderedCanvas = await html2canvas(wrapper, {
                        backgroundColor: null,
                        scale: 1.0,
                        useCORS: false,
                        allowTaint: true,
                        width: Math.max(wrapper.offsetWidth, 1),
                        height: Math.max(wrapper.offsetHeight, 1),
                        logging: false, // keep console clean
                        x: 0,
                        y: 0,
                        scrollX: 0,
                        scrollY: 0,
                        windowWidth: wrapper.offsetWidth,
                        windowHeight: wrapper.offsetHeight,
                        // Disable experimental ForeignObject path to avoid EXPERIMENTAL logs
                        foreignObjectRendering: false,
                        ignoreElements: (element) => {
                            // Skip elements that might cause CORS or document.write() issues
                            if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
                                return true;
                            }
                            
                            // Skip external images (Google profile pics, etc.)
                            if (element.tagName === 'IMG') {
                                const img = element as HTMLImageElement;
                                if (img.src && (
                                    img.src.includes('googleusercontent.com') ||
                                    img.src.includes('googleapis.com') ||
                                    !img.src.startsWith(window.location.origin)
                                )) {
                                    return true;
                                }
                            }
                            
                            // Skip elements with ignore attribute
                            return element.hasAttribute('data-html2canvas-ignore');
                        }
                    });
                } catch (error) {
                    // Structured error log with context for easier debugging
                    const ctx = {
                        poi: this.poi.name,
                        size: { w: wrapper.offsetWidth, h: wrapper.offsetHeight },
                        hasImages: wrapper.querySelectorAll('img').length,
                        hasIframes: wrapper.querySelectorAll('iframe').length,
                        hasExternalImgs: Array.from(wrapper.querySelectorAll('img')).some((img: any) => img.src && !img.src.startsWith(window.location.origin))
                    } as any;
                    console.error('🎨 html2canvas failed → using fallback', { error, ctx });
                    // Fallback to a simple canvas rendering approach
                    renderedCanvas = this.createFallbackCanvas(wrapper, wrapper.offsetWidth, wrapper.offsetHeight);
                }
            }

            document.body.removeChild(renderContainer);

            // 4. Copy to our canvas with validation
            const srcWidth = renderedCanvas.width;
            const srcHeight = renderedCanvas.height;
            
            // Validate canvas dimensions
            if (srcWidth <= 0 || srcHeight <= 0) {
                console.warn(`[CANVAS:${this.poi.name}] Invalid canvas dimensions: ${srcWidth}x${srcHeight}`);
                return;
            }
            
            // Canvas may have been disposed while awaiting render; recreate if needed
            if (!this._canvas || !this._context) {
                this._canvas = document.createElement('canvas');
                this._context = this._canvas.getContext('2d');
                if (!this._context) {
                    console.error(`[CANVAS:${this.poi.name}] Failed to get 2D context after await`);
                    return;
                }
                if (!this._texture) {
                    this._texture = new THREE.CanvasTexture(this._canvas);
                    this._texture.colorSpace = THREE.SRGBColorSpace;
                }
            }

            // Ensure minimum dimensions to avoid WebGL issues
            const minWidth = Math.max(srcWidth, 1);
            const minHeight = Math.max(srcHeight, 1);
            
            this._canvas!.width = minWidth;
            this._canvas!.height = minHeight;
            
            if (this._context) {
                this._context.clearRect(0, 0, minWidth, minHeight);
                
                // Only draw if source canvas has valid content
                if (srcWidth > 0 && srcHeight > 0) {
                    try {
                        this._context.drawImage(renderedCanvas, 0, 0);
                    } catch (error) {
                        console.error(`[CANVAS:${this.poi.name}] Failed to draw image:`, error);
                        // Fallback to solid color
                        this._context.fillStyle = '#ff0000';
                        this._context.fillRect(0, 0, minWidth, minHeight);
                    }
                }
            }

            // 5. Update texture and material AFTER canvas rendering is complete
            this._texture.needsUpdate = true;
            this._renderedText = text;
            
            // Update geometry and material with new texture
            this.updateCanvasGeometry();

        } catch (error) {
            console.error(`❌ [CANVAS:${this.poi.name}] HTML RENDERING FAILED:`, error);
            // Fallback to plain text rendering
            this.updateContentWithPlainText(text);
        }
    }

    /**
     * Plain text canvas rendering (legacy mode)
     */
    private updateContentWithPlainText(text: string): void {
        try {
            if (!this._isInitialized) {
                return;
            }
            // Recreate canvas/context if they were disposed
            if (!this._canvas || !this._context) {
                this._canvas = document.createElement('canvas');
                this._context = this._canvas.getContext('2d');
                if (!this._context) {
                    console.error(`[CANVAS:${this.poi.name}] Failed to get 2D context (plain text)`);
                    return;
                }
                if (!this._texture) {
                    this._texture = new THREE.CanvasTexture(this._canvas);
                    this._texture.colorSpace = THREE.SRGBColorSpace;
                }
            }
            
            const context = this._context;
            const plainText = this.markdownToPlainText(text);
            const lines = plainText.split('\n').filter(line => line.trim() !== '');

            if (lines.length === 0) {
                this._canvas!.width = 1;
                this._canvas!.height = 1;
                this._texture.needsUpdate = true;
                return;
            }

            // --- Core Rendering Setup ---
            // 🔧 FIX: Use same consistent font size as HTML rendering
            const BASE_FONT_SIZE_FOR_CANVAS = 48; // Same as HTML rendering for consistency
            const padding = BASE_FONT_SIZE_FOR_CANVAS * 0.25;
            const vAlign = this.poi.poidata.labelVAlign.value || 'middle';
            const fontFamily = this.poi.poidata.labelFontFamily.value || 'Arial';
            
            // --- 1. Measure Text ---
            context.font = `${BASE_FONT_SIZE_FOR_CANVAS}px ${fontFamily}`;
            let maxWidth = 0;
            lines.forEach(line => {
                const metrics = context.measureText(line.trim());
                if (metrics.width > maxWidth) maxWidth = metrics.width;
            });
            
            const lineHeight = BASE_FONT_SIZE_FOR_CANVAS * 1.4; // Match HTML line-height
            const textWidth = maxWidth;
            const textHeight = lines.length * lineHeight;
            
            // --- 2. Resize Canvas ---
            const canvasWidth = Math.ceil(textWidth + padding * 2);
            const canvasHeight = Math.ceil(textHeight + (lines.length - 1) * (lineHeight * 0.1) + padding * 2);
            this._canvas!.width = canvasWidth;
            this._canvas!.height = canvasHeight;

            // --- 3. Redraw ---
            context.font = `${BASE_FONT_SIZE_FOR_CANVAS}px ${fontFamily}`;
            context.textAlign = 'center';
            context.textBaseline = vAlign as CanvasTextBaseline;

            // Get colors - FIX: Ensure we have proper contrast
            const bgColor = (this.poi.poidata.labelBackgroundColor.value ?? new Color('#00ff00')).toString();
            const textColor = (this.poi.poidata.labelColor.value ?? new Color('#ff0000')).toString();
            const bgMode = this.poi.poidata.labelBackgroundMode.value || 'line';

            // Draw background
            if (bgMode === 'block') {
                context.fillStyle = bgColor;
                context.fillRect(0, 0, canvasWidth, canvasHeight);
            } else {
                context.clearRect(0, 0, canvasWidth, canvasHeight);
            }
            
            // Draw text
            context.fillStyle = textColor;
            const centerX = canvasWidth / 2;

            let startY;
            switch (vAlign) {
                case 'top':
                    startY = padding + (lineHeight / 2);
                    break;
                case 'bottom':
                    startY = canvasHeight - padding - textHeight + (lineHeight / 2);
                    break;
                case 'middle':
                default:
                    startY = (canvasHeight - textHeight) / 2 + (lineHeight / 2);
                    break;
            }

            lines.forEach((line, index) => {
                const yPos = startY + index * (lineHeight * 1.1);
                if (bgMode === 'line') {
                    const lineMetrics = context.measureText(line.trim());
                    
                    // 🎨 Create tight text highlight effect
                    const highlightPadding = 4; // Minimal padding for text highlight
                    const highlightHeight = lineHeight * 0.8; // Slightly smaller than full line height
                    
                    context.fillStyle = bgColor;
                    
                    // Draw rounded rectangle for highlight effect
                    const highlightX = centerX - lineMetrics.width / 2 - highlightPadding;
                    const highlightY = yPos - highlightHeight / 2;
                    const highlightWidth = lineMetrics.width + (highlightPadding * 2);
                    
                    // Create rounded rectangle path
                    const borderRadius = 3;
                    context.beginPath();
                    context.roundRect(highlightX, highlightY, highlightWidth, highlightHeight, borderRadius);
                    context.fill();
                    
                    context.fillStyle = textColor;
                }
                context.fillText(line.trim(), centerX, yPos);
            });

            // Update texture and material AFTER canvas rendering is complete
            this._texture.needsUpdate = true;
            this._renderedText = text;
            
            // Update geometry and material with new texture
            this.updateCanvasGeometry();
            
        } catch (error) {
            console.error(`❌ [CANVAS:${this.poi.name}] UPDATE CONTENT FAILED:`, error);
        }
    }

    /**
     * Update canvas geometry and material after content changes
     */
    private updateCanvasGeometry(): void {
        if (!this._canvas) return;

        // Recreate geometry with proper aspect ratio
        if (this._mesh) {
            // Dispose old geometry
            if (this._geometry) {
                this._geometry.dispose();
            }
            
            // Create new geometry with correct aspect ratio
            this._geometry = this.createGeometry();
            (this._mesh as THREE.Mesh).geometry = this._geometry;
        }

        // Update material
        if (this._mesh && (this._mesh as THREE.Mesh).material) {
            const material = (this._mesh as THREE.Mesh).material as THREE.MeshBasicMaterial;
            
            // Try to apply canvas texture
            if (this._canvas && this._canvas.width > 0 && this._canvas.height > 0) {
                // Force texture recreation to ensure it has the latest canvas content
                if (this._texture) {
                    this._texture.dispose();
                }
                this._texture = new THREE.CanvasTexture(this._canvas);
                this._texture.colorSpace = THREE.SRGBColorSpace;
                this._texture.needsUpdate = true;
                
                material.map = this._texture;
                material.color.setHex(0xffffff);
                
                //console.log(`✅ [CANVAS:${this.poi.name}] Updated material with new texture (${this._canvas.width}x${this._canvas.height})`);
            } else {
                // Fallback to red color if texture fails
                material.map = null;
                material.color.setHex(0xff0000);
                console.warn(`❌ [CANVAS:${this.poi.name}] Invalid canvas dimensions, using fallback color`);
            }
            
            material.opacity = 1.0; // Opacity will be handled in onUpdate
            material.transparent = true; // Always transparent for opacity updates
            material.needsUpdate = true;
        }
        
        // CRITICAL: Update scale AFTER canvas is rendered with correct content
        // This ensures getAspectRatio() returns the correct value
        this._updateScale();
    }

    /**
     * Set rendering mode (HTML vs plain text)
     */
    public setHtmlRenderingEnabled(enabled: boolean): void {
        this._useHtmlRendering = enabled;
        // Re-render with current text to apply the new mode
        if (this._renderedText) {
            this.updateContent(this._renderedText);
        }
    }

    /**
     * Check if HTML rendering is enabled
     */
    public isHtmlRenderingEnabled(): boolean {
        return this._useHtmlRendering;
    }

    /**
     * Returns the aspect ratio of the rendered canvas.
     */
    public getAspectRatio(): number {
        if (!this._canvas || this._canvas.height === 0) {
            return 1;
        }
        return this._canvas.width / this._canvas.height;
    }

    /**
     * Update style properties
     */
    updateStyle(): void {
        if (!this._isInitialized) return;
        
        // Get the HTML content (auto-generated from markdown)
        const html = this.poi.poidata.html.value || '';
        
        // Re-rendering the content will apply the new styles
        this.updateContent(html);
    }

    /**
     * Apply opacity to the material
     */
    protected applyOpacityToMaterial(opacity: number): void {
        if (this._mesh && (this._mesh as THREE.Mesh).material) {
            const material = (this._mesh as THREE.Mesh).material as THREE.MeshBasicMaterial;
            
            if (material.opacity !== opacity) {
                material.opacity = opacity;
                material.needsUpdate = true;
            }
        }
    }

    public show(): void {
        this.setVisible(true);
    }

    public hide(): void {
        this.setVisible(false);
    }

    /**
     * Override onUpdate - simplified without redundant opacity handling
     */
    onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime); // This calls _updateOpacity which now handles material updates
    }

    /**
     * Get the canvas element for Three.js texture creation
     */
    getCanvas(): HTMLCanvasElement | null {
        return this._canvas;
    }

    /**
     * Create or update texture for Three.js
     */
    createTexture(): any {
        if (!this._canvas) return null;
        
        if (!this._texture) {
            this._texture = new THREE.CanvasTexture(this._canvas);
        }
        this._texture.needsUpdate = true;
        return this._texture;
    }

    /**
     * Get render type
     */
    getRenderType(): LabelMeshType.CANVAS {
        return LabelMeshType.CANVAS;
    }

    /**
     * Get actual canvas width for scaling
     */
    protected getCanvasWidth(): number {
        return this._canvas?.width ?? 1;
    }

    /**
     * Get actual canvas height for scaling
     */
    protected getCanvasHeight(): number {
        return this._canvas?.height ?? 1;
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        // Dispose canvas-specific resources
        if (this._canvas) {
            // Clean up canvas
            this._canvas.width = 1;
            this._canvas.height = 1;
            this._canvas = null;
            this._context = null;
        }

        if (this._texture) {
            this._texture.dispose();
            this._texture = null;
        }

        this._isInitialized = false;
    }
} 